import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { filter } from 'rxjs';
import { UserStore } from '@bn/auth/stores';
import { MixpanelEventsDirective } from '@bn/shared/utils/directives';
import { ButtonComponent } from '@bn/web-ui/button/button.component';
import { CheckboxListComponent } from '@bn/web-ui/checkbox-list/checkbox-list.component';
import { FormListItem } from '@bn/web-ui/models';
import { ActiveSurvey, MatchedSurveyField, SurveyAnswer, SurveyWidgetService } from '../../services/survey-widget.service';

@Component({
  selector: 'bn-survey-widget',
  templateUrl: './survey-widget.component.html',
  styleUrls: ['./survey-widget.component.scss'],
  imports: [ButtonComponent, CheckboxListComponent, FaIconComponent, FormsModule],
})
export class SurveyWidgetComponent implements OnInit {
  #router = inject(Router);
  #service = inject(SurveyWidgetService);
  #userStore = inject(UserStore);
  #destroyRef = inject(DestroyRef);
  #mixpanel = inject(MixpanelEventsDirective);

  survey = signal<ActiveSurvey | null>(null);
  visible = signal(false);
  currentStep = signal(0);
  answers = signal<Record<string, string | string[]>>({});
  submitting = signal(false);
  submitted = signal(false);

  // Surveys eligible for this user, fetched once at startup. Mutated in place
  // when a survey is consumed (submitted or dismissed) so we never re-show.
  #activeSurveys: ActiveSurvey[] = [];

  currentStepData = computed(() => {
    const s = this.survey();
    if (!s) return null;
    const sorted = [...s.steps].sort((a, b) => a.position - b.position);
    return sorted[this.currentStep()] ?? null;
  });

  totalSteps = computed(() => this.survey()?.steps.length ?? 0);
  validationAttempted = signal(false);

  ngOnInit(): void {
    if (!this.#service.isAvailable()) return;

    // One-shot: fetch every survey the user is eligible for. After this,
    // matching against the current URL is purely client-side.
    const userDetails = this.#userStore.userDetails();
    this.#service
      .loadActiveSurveys(userDetails?.CategoryType, userDetails?.BillingType)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: (response) => {
          this.#activeSurveys = response.surveys ?? [];
          // Run a first check against the URL we're already on
          this.#checkForSurvey(this.#router.url);
        },
        error: () => {
          // Silently ignore — survey matching should never block the dashboard
        },
      });

    // From here on, every navigation is matched locally against the cached list
    this.#router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((event) => {
        this.#checkForSurvey(event.urlAfterRedirects);
      });
  }

  #delayTimer: ReturnType<typeof setTimeout> | null = null;

  #checkForSurvey(url: string): void {
    // Skip if a survey is already on screen or we have nothing cached
    if (this.visible() || this.#activeSurveys.length === 0) return;

    // Clear any pending delay timer in case the user navigated away before
    // the previous match's delay elapsed.
    if (this.#delayTimer) {
      clearTimeout(this.#delayTimer);
      this.#delayTimer = null;
    }

    const deviceType = this.#service.getDeviceType();
    const match = this.#activeSurveys.find((s) => (s.device_type === 'all' || s.device_type === deviceType) && this.#service.matchesUrl(s.url_pattern, url));
    if (!match) return;

    this.survey.set(match);
    const delaySec = match.delay_seconds || 0;
    if (delaySec > 0) {
      this.#delayTimer = setTimeout(() => this.#show(), delaySec * 1000);
    } else {
      this.#show();
    }
  }

  // Remove a survey from the cache once the user has acted on it so it can
  // never be matched again in this session.
  #consume(surveyId: string): void {
    this.#activeSurveys = this.#activeSurveys.filter((s) => s.id !== surveyId);
  }

  #show(): void {
    this.currentStep.set(0);
    this.answers.set({});
    this.submitted.set(false);
    this.submitting.set(false);
    this.visible.set(true);

    const s = this.survey();
    if (s) {
      this.#mixpanel.addEvent('Survey Shown', {
        surveyId: s.id,
        surveyName: s.name,
      });
    }
  }

  dismiss(): void {
    const s = this.survey();
    if (s) {
      this.#mixpanel.addEvent('Survey Dismissed', {
        surveyId: s.id,
        surveyName: s.name,
        step: this.currentStep() + 1,
      });
      this.#service.dismissSurvey(s.id).pipe(takeUntilDestroyed(this.#destroyRef)).subscribe();
      this.#consume(s.id);
    }
    this.visible.set(false);
    this.survey.set(null);
  }

  prevStep(): void {
    if (this.currentStep() > 0) {
      this.currentStep.update((s) => s - 1);
    }
  }

  nextStep(): void {
    if (!this.#validateCurrentStep()) {
      this.validationAttempted.set(true);
      return;
    }

    if (this.currentStep() < this.totalSteps() - 1) {
      this.currentStep.update((s) => s + 1);
    }
  }

  setAnswer(fieldId: string, value: string | string[]): void {
    this.answers.update((prev) => ({ ...prev, [fieldId]: value }));
  }

  getAnswer(fieldId: string): string | string[] | undefined {
    return this.answers()[fieldId];
  }

  toggleMultiChoice(fieldId: string, option: string): void {
    const current = (this.getAnswer(fieldId) as string[]) || [];
    const updated = current.includes(option) ? current.filter((o) => o !== option) : [...current, option];
    this.setAnswer(fieldId, updated);
  }

  isMultiChoiceSelected(fieldId: string, option: string): boolean {
    const current = (this.getAnswer(fieldId) as string[]) || [];
    return current.includes(option);
  }

  // ── Checkbox-list helpers for multi_choice ──

  #checkboxItemsCache = new Map<string, FormListItem[]>();
  #checkboxValueCache = new Map<string, Record<string, boolean>>();

  getCheckboxItems(field: MatchedSurveyField): FormListItem[] {
    let items = this.#checkboxItemsCache.get(field.id);
    if (!items) {
      items = (field.options ?? []).map((option) => ({ name: option, label: option, value: false }));
      this.#checkboxItemsCache.set(field.id, items);
    }
    return items;
  }

  getCheckboxValue(fieldId: string): Record<string, boolean> {
    const selected = (this.getAnswer(fieldId) as string[]) || [];
    const cached = this.#checkboxValueCache.get(fieldId);
    if (cached) {
      const cachedKeys = Object.keys(cached).filter((k) => cached[k]);
      if (cachedKeys.length === selected.length && cachedKeys.every((k) => selected.includes(k))) {
        return cached;
      }
    }
    const map: Record<string, boolean> = {};
    for (const opt of selected) {
      map[opt] = true;
    }
    this.#checkboxValueCache.set(fieldId, map);
    return map;
  }

  onCheckboxChange(fieldId: string, value: Record<string, boolean>): void {
    const selected = Object.entries(value)
      .filter(([, checked]) => checked)
      .map(([name]) => name);
    this.setAnswer(fieldId, selected);
  }

  // ── Rating helpers ──

  readonly smileyIcons: { value: string; icon: IconName; label: string }[] = [
    { value: '1', icon: 'face-angry', label: 'Very dissatisfied' },
    { value: '2', icon: 'face-frown', label: 'Dissatisfied' },
    { value: '3', icon: 'face-meh', label: 'Neutral' },
    { value: '4', icon: 'face-smile', label: 'Satisfied' },
    { value: '5', icon: 'face-grin-stars', label: 'Very satisfied' },
  ];

  getRatingStyle(field: MatchedSurveyField): 'numbers' | 'smileys' | 'thumbs' {
    const opt = field.options?.[0];
    if (!opt) return 'numbers';
    if (opt === 'smileys') return 'smileys';
    if (opt === 'thumbs') return 'thumbs';
    return 'numbers';
  }

  getRatingScale(field: MatchedSurveyField): number[] {
    const opt = field.options?.[0];
    let count = 5;
    if (opt?.startsWith('numbers:')) {
      const parsed = parseInt(opt.split(':')[1], 10);
      if (!isNaN(parsed) && parsed >= 2) count = parsed;
    }
    return Array.from({ length: count }, (_, i) => i + 1);
  }

  submit(): void {
    if (!this.#validateCurrentStep()) {
      this.validationAttempted.set(true);

      return;
    }

    const s = this.survey();
    if (!s) return;

    const allAnswers: SurveyAnswer[] = Object.entries(this.answers()).map(([field_id, value]) => ({
      field_id,
      value,
    }));

    this.submitting.set(true);

    this.#service
      .submitResponse(s.id, allAnswers)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          const fieldMap = new Map(s.steps.flatMap((step) => step.fields.map((f) => [f.id, f.label])));
          const answersObj: Record<string, string> = {};
          for (const [fieldId, value] of Object.entries(this.answers())) {
            const label = fieldMap.get(fieldId) ?? fieldId;
            answersObj[label] = Array.isArray(value) ? value.join(', ') : value;
          }
          this.#mixpanel.addEvent('Survey Answered', {
            surveyId: s.id,
            surveyName: s.name,
            answers: JSON.stringify(answersObj),
          });
          this.submitted.set(true);
          this.submitting.set(false);
          this.#consume(s.id);
          if (s.auto_close) {
            setTimeout(() => {
              this.visible.set(false);
              this.survey.set(null);
            }, 2000);
          }
        },
        error: () => {
          this.submitting.set(false);
        },
      });
  }

  #validateCurrentStep(): boolean {
    const step = this.currentStepData();
    if (!step) return false;

    for (const field of step.fields) {
      if (field.is_required) {
        const answer = this.getAnswer(field.id);
        if (!answer || (Array.isArray(answer) && answer.length === 0) || (typeof answer === 'string' && !answer.trim())) {
          return false;
        }
      }
    }
    return true;
  }

  isFieldInvalid(fieldId: string, isRequired: boolean): boolean {
    if (!this.validationAttempted() || !isRequired) return false;
    const answer = this.getAnswer(fieldId);
    return !answer || (Array.isArray(answer) && answer.length === 0) || (typeof answer === 'string' && !answer.trim());
  }
}
